// src/services/referralRowReview.js
//
// Firebase-free "does this referral row need a human glance?" check for the
// Mass Text page. WARNING ONLY: nothing here blocks Schedule or Send — it only
// decides whether a row shows "Needs review" and why.
//
// Reasons come from two places:
//   - the row's own data (missing date/time/location, a time with no AM/PM),
//     which keeps showing until the field is actually fixed; and
//   - the warnings the schedule reader attached on import (unknown location
//     code, assumed AM/PM, low read confidence, missing MRN). Saving an edit
//     clears those — editing a row counts as having reviewed it.
//
// Reasons are short labels with no patient data in them.

// "3:00" — a time with no AM/PM. The server assumes clinic hours (1-6 means
// PM), but a typed or edited time never carries a warning, so flag it here.
export const hasNoMeridiem = (time) => {
  const s = String(time || "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m || m[3]) return false;
  const hh = parseInt(m[1], 10);
  return hh >= 1 && hh <= 12;
};

// Extractor warnings already represented elsewhere on the card, or covered by
// a data-based reason below — so they aren't counted twice.
const SKIP_WARNING = [
  /no valid mobile/i, // red "No phone — not sendable" badge
  /missing or unreadable appointment date/i,
  /missing or unreadable appointment time/i,
];

// Map an extractor warning to a short reason label.
const warningReason = (w) => {
  const s = String(w || "");
  if (SKIP_WARNING.some((re) => re.test(s))) return "";
  if (/unmapped location code/i.test(s)) return "Unknown location code";
  if (/am\/pm not shown/i.test(s)) return s.replace(/^AM\/PM not shown — /i, "AM/PM missing — ");
  if (/low read confidence/i.test(s)) return "Low read confidence";
  if (/missing mrn/i.test(s)) return "Missing MRN";
  return s;
};

// Short reasons this row deserves a look. Empty array = looks fine.
export function reviewReasons(row) {
  const r = row || {};
  const reasons = [];
  const add = (x) => {
    if (x && !reasons.includes(x)) reasons.push(x);
  };

  if (!String(r.appointmentDate || "").trim()) add("Missing date");
  if (!String(r.appointmentTime || "").trim()) add("Missing time");
  else if (hasNoMeridiem(r.appointmentTime)) add("AM/PM missing");
  if (!String(r.locationCode || "").trim()) add("Missing location");
  if (r.source === "schedule_pdf" && !String(r.mrn || "").trim()) add("Missing MRN");

  for (const w of Array.isArray(r.warnings) ? r.warnings : []) {
    const reason = warningReason(w);
    // The data check already said "AM/PM missing"; the import's more specific
    // "assumed PM" label replaces it rather than duplicating.
    if (reason.startsWith("AM/PM missing")) {
      const i = reasons.indexOf("AM/PM missing");
      if (i >= 0) reasons.splice(i, 1);
    }
    add(reason);
  }
  return reasons;
}
